<template>
  <div class="hero-slider-fullscreen">
    <div class="slider-container-fullscreen">
      <!-- Main Full-Bleed Slider Extending to Top Under Floating Header (Reduced by 20% Height) -->
      <div class="slider-main-fullscreen" @mouseenter="pauseAutoPlay" @mouseleave="resumeAutoPlay">
        <div
          class="slides-wrapper-fullscreen"
          :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
        >
          <div v-for="(slide, index) in slides" :key="index" class="slide-fullscreen">
            <a :href="getSlideLink(slide)" class="slide-link-fullscreen">
              <img
                :src="slide.image"
                :alt="slide.title || 'Banner'"
                :loading="index === 0 ? 'eager' : 'lazy'"
                :fetchpriority="index === 0 ? 'high' : 'auto'"
                class="slide-img-fullscreen"
              />
            </a>
          </div>
        </div>

        <!-- Slider Dots (Desktop Only - Hidden on Mobile) -->
        <div v-if="slides.length > 1" class="slider-dots-fullscreen desktop-only-dots">
          <button
            v-for="(_, index) in slides"
            :key="index"
            @click.stop="goToSlide(index)"
            class="dot-fullscreen"
            :class="{ active: currentSlide === index }"
            :aria-label="`Go to slide ${index + 1}`"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  categories: {
    type: Array,
    default: () => []
  },
  sliders: {
    type: Array,
    default: () => []
  },
  heroBanner: {
    type: Object,
    default: null
  }
})

const currentSlide = ref(0)
let autoPlayInterval = null

const slides = computed(() => {
  if (props.sliders && props.sliders.length > 0) {
    return props.sliders
  }
  if (props.heroBanner && props.heroBanner.image) {
    return [{
      id: props.heroBanner.id || 'hero-1',
      title: props.heroBanner.title || 'Banner',
      image: props.heroBanner.image,
      link: props.heroBanner.link || props.heroBanner.url || '/trending'
    }]
  }
  // No images uploaded — show nothing
  return []
})

const getSlideLink = (slide) => {
  if (!slide) return '/trending'
  return slide.link || slide.url || slide.target_url || '/trending'
}

const nextSlide = () => {
  if (slides.value.length <= 1) return
  currentSlide.value = (currentSlide.value + 1) % slides.value.length
}

const prevSlide = () => {
  if (slides.value.length <= 1) return
  currentSlide.value = currentSlide.value === 0 ? slides.value.length - 1 : currentSlide.value - 1
}

const goToSlide = (index) => {
  currentSlide.value = index
}

const startAutoPlay = () => {
  if (slides.value.length <= 1) return
  stopAutoPlay()
  autoPlayInterval = setInterval(() => {
    nextSlide()
  }, 5000)
}

const stopAutoPlay = () => {
  if (autoPlayInterval) {
    clearInterval(autoPlayInterval)
    autoPlayInterval = null
  }
}

const pauseAutoPlay = () => {
  stopAutoPlay()
}

const resumeAutoPlay = () => {
  startAutoPlay()
}

onMounted(() => {
  startAutoPlay()
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<style scoped>
/* ====================================================
   HERO SLIDER (20% REDUCED HEIGHT, NO ARROWS, SPACE BELOW)
   ==================================================== */
.hero-slider-fullscreen {
  width: 100%;
  /* Mirrors .header-container exactly: max-width 1400px + 1.75rem side padding = same width as menu bar */
  max-width: 1400px;
  margin: 0 auto 1.75rem !important;
  padding: 0;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

/* Hide entire slider block when there are no slides */
.hero-slider-fullscreen:empty {
  display: none !important;
  margin: 0 !important;
}

.slider-container-fullscreen {
  width: 100%;
  margin: 0;
  padding: 0;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: none;
  background: transparent;
}

.slider-main-fullscreen {
  position: relative;
  width: 100%;
  /* Height follows the image's natural ratio — image is never cropped */
  overflow: hidden;
  border-radius: 22px;
  background: transparent;
}

.slides-wrapper-fullscreen {
  display: flex;
  width: 100%;
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-fullscreen {
  flex: 0 0 100%;
  width: 100%;
  position: relative;
}

.slide-link-fullscreen {
  display: block;
  width: 100%;
  text-decoration: none;
  position: relative;
}

.slide-img-fullscreen {
  /* Natural image ratio at every screen size — scales, never crops */
  width: 100%;
  height: auto;
  display: block;
}

/* --- Fullscreen Dots (Desktop Only) --- */
.slider-dots-fullscreen {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  padding: 6px 14px;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.dot-fullscreen {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.dot-fullscreen:hover {
  background: rgba(255, 255, 255, 0.85);
}

.dot-fullscreen.active {
  width: 24px;
  border-radius: 9999px;
  background: #ffffff;
}

/* --- Responsive Breakpoints ---
   Desktop: fixed 16:6 banner shape (1920×720) — compact height.
   Mobile: natural image ratio — scales cleanly, never cropped. */
@media (min-width: 869px) {
  .slider-main-fullscreen {
    aspect-ratio: 16 / 6;
    max-height: 70vh;
  }
  .slides-wrapper-fullscreen,
  .slide-fullscreen,
  .slide-link-fullscreen {
    height: 100%;
  }
  .slide-img-fullscreen {
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
}

@media (max-width: 1024px) {
  .hero-slider-fullscreen {
    margin-bottom: 1.5rem !important;
  }
}

@media (max-width: 868px) {
  .hero-slider-fullscreen {
    padding: 0;
  }
  .slider-container-fullscreen,
  .slider-main-fullscreen {
    border-radius: 0;
  }
}

@media (max-width: 768px) {
  .hero-slider-fullscreen {
    margin-bottom: 1.25rem !important;
  }
  .desktop-only-dots,
  .slider-dots-fullscreen {
    display: none !important;
  }
}

@media (max-width: 480px) {
  .hero-slider-fullscreen {
    margin-bottom: 1rem !important;
  }
}
</style>
